// Detection rules for the four admin-structure / policy alert kinds added by
// KAN-661/662 (RD-SEC-046/047). Split from securityEventRules.js for the
// 500-line cap; re-exported by securityEvents.js. Pure — no I/O. The one
// outside fact (a deactivated user's role) arrives as `roleByUserId`
// (Map<userId, role>) read from tenant_memberships by the caller.
//
// Alert ids follow the stable receipt-key form `<kind>:<audit row uuid>`
// (no emails/names — receipts hold ids + timestamps only).

function sameSet(a, b) {
  return a.length === b.length && a.every((x) => b.includes(x));
}

const asRoleList = (v) => (Array.isArray(v) ? v.filter((r) => typeof r === 'string') : []);

// employee/ownership_transferred — payload {old_owner, new_owner} are user ids
// only (migration 20261002110100). Names are filled by the caller from the
// directory: `fromUserId` / `toUserId` mark which ids to resolve.
export function detectOwnershipTransferred(rows) {
  return (rows || []).map((row) => ({
    id: `ownership_transferred:${row.id}`,
    kind: 'ownership_transferred',
    severity: 'high',
    actorId: row.actor_id || null,
    nameRefs: { from: row.payload?.old_owner || row.actor_id || null, to: row.payload?.new_owner || row.entity_id || null },
    headline: {
      id: 'activity.alerts.ownershipTransferred',
      values: { from: row.actor_name || null, to: null, actor: row.actor_name || null },
    },
    lastSeen: row.created_at,
    activity: { entityType: 'employee', eventType: 'ownership_transferred' },
  }));
}

// employee/deactivated — ONLY when the target is an admin (role from the
// tenant's memberships; unknown → skipped, never guessed).
export function detectAdminDeactivated(rows, roleByUserId) {
  return (rows || [])
    .filter((row) => row?.entity_id && roleByUserId?.get(row.entity_id) === 'admin')
    .map((row) => ({
      id: `admin_deactivated:${row.id}`,
      kind: 'admin_deactivated',
      severity: 'high',
      actorId: row.actor_id || null,
      subjectUserId: row.entity_id,
      headline: {
        id: 'activity.alerts.adminDeactivated',
        values: { target: row.payload?.employee_name || null, actor: row.actor_name || null },
      },
      lastSeen: row.created_at,
      activity: { entityType: 'employee', eventType: 'deactivated' },
    }));
}

// employee/deleted — ONLY when payload.removed_role === 'admin'.
export function detectAdminRemoved(rows) {
  return (rows || [])
    .filter((row) => row?.payload?.removed_role === 'admin')
    .map((row) => ({
      id: `admin_removed:${row.id}`,
      kind: 'admin_removed',
      severity: 'high',
      actorId: row.actor_id || null,
      headline: {
        id: 'activity.alerts.adminRemoved',
        values: { target: row.payload?.employee_name || null, actor: row.actor_name || null },
      },
      lastSeen: row.created_at,
      activity: { entityType: 'employee', eventType: 'deleted' },
    }));
}

// settings/mfa_policy_changed — payload {required_roles, previous}. An alert
// only when the SET of roles actually changed; details carries what moved.
export function detectMfaPolicyChanged(rows) {
  const events = [];
  (rows || []).forEach((row) => {
    const next = asRoleList(row?.payload?.required_roles);
    const prev = asRoleList(row?.payload?.previous);
    if (sameSet(next, prev)) return;
    events.push({
      id: `mfa_policy_changed:${row.id}`,
      kind: 'mfa_policy_changed',
      severity: 'medium',
      actorId: row.actor_id || null,
      headline: { id: 'activity.alerts.mfaPolicyChanged', values: { actor: row.actor_name || null } },
      details: {
        addedRoles: next.filter((r) => !prev.includes(r)),
        removedRoles: prev.filter((r) => !next.includes(r)),
      },
      lastSeen: row.created_at,
      activity: { entityType: 'settings', eventType: 'mfa_policy_changed' },
    });
  });
  return events;
}
